/**
 * Trilha de auditoria — somente administrador.
 *
 * A paginação é do servidor, não da tela: a tabela cresce sem parar e trazê-la
 * inteira para o navegador deixaria de funcionar no primeiro mês de uso.
 */
import { useCallback, useEffect, useState } from 'react';
import { mensagemErro } from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import IntegracoesBioslab from '../components/IntegracoesBioslab';
import { filtrosAuditoria, listarAuditoria, resumoAuditoria } from '../api/fluxfin';
import {
  Ajuda,
  Alerta,
  Botao,
  Busca,
  Indicador,
  LoadingSpinner,
  PageHeader,
  Paginacao,
  Painel,
  Tabela,
  Th,
  Vazio,
} from '../components/common';
import { useDebounce } from '../hooks/useDebounce';
import { formatDateTime, numero } from '../utils/formato';
import { PERFIL } from '../utils/opcoes';

const TAMANHO = 50;

export default function Auditoria() {
  const { isAdmin } = useAuth();
  const [aba, setAba] = useState('trilha');
  const [dados, setDados] = useState({ itens: [], total: 0, pagina: 1, total_paginas: 1 });
  const [resumo, setResumo] = useState(null);
  const [opcoes, setOpcoes] = useState({ entidades: [], acoes: [], autores: [] });

  const [entidade, setEntidade] = useState('');
  const [acao, setAcao] = useState('');
  const [autor, setAutor] = useState('');
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const buscaLenta = useDebounce(busca, 400);

  useEffect(() => {
    filtrosAuditoria()
      .then((f) => setOpcoes(f.data))
      .catch(() => {});
    // O resumo é do sistema inteiro, e por isso é do admin. Para o gestor
    // nem é pedido: chamar e engolir o 403 funcionaria, mas deixa a tela
    // dependendo de um erro para se comportar direito.
    if (isAdmin) {
      resumoAuditoria()
        .then((r) => setResumo(r.data))
        .catch(() => {});
    }
  }, [isAdmin]);

  // Trocar qualquer filtro volta para a primeira página — senão a tela fica
  // vazia por estar numa página que o novo filtro não tem.
  useEffect(() => setPagina(1), [entidade, acao, autor, buscaLenta]);

  const carregar = useCallback(() => {
    setCarregando(true);
    listarAuditoria({
      entidade: entidade || undefined,
      acao: acao || undefined,
      id_usuario: autor || undefined,
      busca: buscaLenta || undefined,
      pagina,
      tamanho: TAMANHO,
    })
      .then((r) => setDados(r.data))
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível carregar a auditoria.')))
      .finally(() => setCarregando(false));
  }, [entidade, acao, autor, buscaLenta, pagina]);

  useEffect(carregar, [carregar]);

  const limpar = () => {
    setEntidade('');
    setAcao('');
    setAutor('');
    setBusca('');
  };

  const temFiltro = entidade || acao || autor || busca;
  const inicio = dados.total === 0 ? 0 : (dados.pagina - 1) * TAMANHO + 1;
  const fim = Math.min(dados.pagina * TAMANHO, dados.total);

  return (
    <>
      <PageHeader
        titulo={isAdmin ? 'Auditoria' : 'Minhas ações'}
        subtitulo={
          isAdmin
            ? 'Tudo que foi feito no sistema, por quem e quando. Registro somente de leitura.'
            : 'As ações que você praticou, por data. Registro somente de leitura — nem você nem ninguém altera o que já foi gravado.'
        }
      />

      {/* A auditoria das INTEGRAÇÕES continua exclusiva do admin — ela
          mostra o acesso à produção de um terceiro, e o endpoint recusa o
          gestor. Sem abas, a tela do gestor não finge que há mais. */}
      {isAdmin && (
        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${aba === 'trilha' ? 'active' : ''}`}
              onClick={() => setAba('trilha')}
            >
              Trilha de ações
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${aba === 'integracoes' ? 'active' : ''}`}
              onClick={() => setAba('integracoes')}
            >
              Integrações com o Bioslab
            </button>
          </li>
        </ul>
      )}

      {isAdmin && aba === 'integracoes' && <IntegracoesBioslab />}

      {aba === 'trilha' && (
        <>

      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>

      {resumo && (
        <div className="ff-grid-tiles mb-4">
          <Indicador
            natureza="financeiro"
            rotulo="Registros na trilha"
            valor={numero(resumo.total)}
            nota="desde o início do uso"
          />
          <Indicador
            natureza="neutro"
            rotulo="Último registro"
            valor={
              <span style={{ fontSize: 17 }}>
                {resumo.ultimo_registro ? formatDateTime(resumo.ultimo_registro) : '—'}
              </span>
            }
          />
          {(resumo.por_acao || []).slice(0, 2).map((a) => (
            <Indicador
              key={a.acao}
              natureza="entrada"
              rotulo={a.label}
              valor={numero(a.qtd)}
              nota="ocorrências"
            />
          ))}
        </div>
      )}

      <Painel
        titulo="Registros"
        subtitulo={
          dados.total > 0
            ? `${numero(dados.total)} registro${dados.total === 1 ? '' : 's'}${temFiltro ? ' (filtrado)' : ''}`
            : undefined
        }
        busca={
          <>
            <Busca
              valor={busca}
              onChange={setBusca}
              placeholder="Buscar na descrição"
              id="busca-auditoria"
            />
            <select
              className="ff-select"
              style={{ width: 'auto', height: 38 }}
              value={entidade}
              onChange={(e) => setEntidade(e.target.value)}
              aria-label="Filtrar por tipo de registro"
            >
              <option value="">Todos os tipos</option>
              {opcoes.entidades.map((o) => (
                <option key={o.valor} value={o.valor}>
                  {o.label}
                </option>
              ))}
            </select>
            <select
              className="ff-select"
              style={{ width: 'auto', height: 38 }}
              value={acao}
              onChange={(e) => setAcao(e.target.value)}
              aria-label="Filtrar por ação"
            >
              <option value="">Todas as ações</option>
              {opcoes.acoes.map((o) => (
                <option key={o.valor} value={o.valor}>
                  {o.label}
                </option>
              ))}
            </select>
            <select
              className="ff-select"
              style={{ width: 'auto', height: 38 }}
              value={autor}
              onChange={(e) => setAutor(e.target.value)}
              aria-label="Filtrar por usuário"
            >
              <option value="">Todos os usuários</option>
              {opcoes.autores.map((o) => (
                <option key={o.valor} value={o.valor}>
                  {o.label}
                </option>
              ))}
            </select>
            {temFiltro && (
              <Botao variante="fantasma" pequeno icone="bi-x-lg" onClick={limpar}>
                Limpar
              </Botao>
            )}
          </>
        }
        rodape={
          dados.total > 0 && (
            <Paginacao
              pagina={dados.pagina}
              totalPaginas={dados.total_paginas}
              total={dados.total}
              de={inicio}
              ate={fim}
              tamanho={TAMANHO}
              irPara={setPagina}
              rotulo="registros"
            />
          )
        }
      >
        {carregando ? (
          <LoadingSpinner />
        ) : dados.itens.length === 0 ? (
          <Vazio
            icone="bi-clipboard-check"
            titulo={temFiltro ? 'Nenhum registro com esses filtros' : 'Nenhum registro ainda'}
            descricao={
              temFiltro
                ? 'Ajuste ou limpe os filtros.'
                : 'As ações feitas no sistema aparecem aqui automaticamente.'
            }
          />
        ) : (
          <Tabela>
            <thead>
              <tr>
                <Th>
                  Quando
                  <Ajuda titulo="Momento do registro">
                    Gravado junto com a operação, na mesma transação. Se a operação falha e
                    é desfeita, o registro não fica.
                  </Ajuda>
                </Th>
                <Th>Quem</Th>
                <Th>Perfil</Th>
                <Th>Tipo</Th>
                <Th>Ação</Th>
                <Th>Descrição</Th>
                <Th>
                  Registro
                  <Ajuda titulo="Identificador do registro afetado">
                    O código interno do item que sofreu a ação — útil para rastrear o mesmo
                    registro ao longo do tempo.
                  </Ajuda>
                </Th>
              </tr>
            </thead>
            <tbody>
              {dados.itens.map((r) => {
                const perfil = PERFIL[r.perfil];
                return (
                  <tr key={r.id}>
                    <td className="col-meta">{formatDateTime(r.criado_em)}</td>
                    <td className="col-principal">{r.email || 'sistema'}</td>
                    <td className="col-meta">{perfil?.label || r.perfil || '—'}</td>
                    <td className="col-meta">{r.entidade_label}</td>
                    <td>
                      <span className="ff-badge ff-badge--conferencia">{r.acao_label}</span>
                    </td>
                    <td className="col-texto" style={{ fontSize: 13 }}>
                      {r.descricao || '—'}
                    </td>
                    <td className="col-meta" style={{ fontSize: 12 }}>
                      {r.id_entidade ? `${String(r.id_entidade).slice(0, 8)}…` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Tabela>
        )}
      </Painel>
        </>
      )}
    </>
  );
}
